import React, { useState, useMemo } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebaseConfig';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { validatePassword, getPasswordStrength } from '../passwordValidator';

const PASSWORD_REQUIREMENTS = [
  { label: 'At least 12 characters', test: (p: string) => p.length >= 12 },
  { label: 'Uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { label: 'Lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { label: 'Number', test: (p: string) => /[0-9]/.test(p) },
  { label: 'Special character (!@#$%^&*...)', test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

export default function Signup() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => getPasswordStrength(password), [password]);
  const passwordMismatch =
    confirmPassword.length > 0 && password !== confirmPassword;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.isValid) {
      setError('Password does not meet requirements: ' + passwordCheck.errors.join(', '));
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // Server creates the Auth user, sets vendor custom claims, and writes
      // the vendors/{uid} doc atomically. We sign in afterward to get the
      // session and trigger the verification email.
      const functions = getFunctions();
      const vendorSignup = httpsCallable(functions, 'vendorSignup');
      await vendorSignup({
        email: email.trim().toLowerCase(),
        password,
        companyName: companyName.trim(),
      });

      const cred = await signInWithEmailAndPassword(auth, email.trim().toLowerCase(), password);

      // Send verification email via our own Resend domain rather than
      // Firebase's built-in mailer, which sends from a shared Google domain
      // that Gmail treats as spam. Errors here are non-fatal — the partner
      // can request a resend from the dashboard banner.
      try {
        const sendVerification = httpsCallable(functions, 'sendVendorVerificationEmail');
        await sendVerification({ continueUrl: `${window.location.origin}/login` });
      } catch (verifyErr) {
        console.warn('sendVendorVerificationEmail failed:', verifyErr);
      }

      // Force refresh so the freshly-set role/vendorId claims land on the
      // ID token before we navigate into the partner-gated routes.
      const tokenResult = await cred.user.getIdTokenResult(true);
      const vendorId = tokenResult.claims.vendorId as string | undefined;
      if (!vendorId) {
        setError('Account created but role assignment is missing. Please try logging in.');
        return;
      }

      navigate('/vendor/dashboard', { state: { vendorId } });
    } catch (err: any) {
      const message = err?.message || 'Signup failed. Please try again.';
      setError(message.replace(/^Firebase:\s*/, '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          mt: { xs: 4, sm: 6 },
          mb: { xs: 4, sm: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '2.125rem' },
            textAlign: 'center',
          }}
        >
          ARCS Partner Signup
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mb: 2, textAlign: 'center' }}
        >
          Create your account to start listing parking lots on ARCS.
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            margin="normal"
            required
            autoComplete="organization"
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
            helperText="We'll send a verification link to this address."
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
          />

          {password.length > 0 && (
            <Box sx={{ mt: 1, mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, strength.score)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': { bgcolor: strength.color },
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: strength.color, fontWeight: 600 }}
              >
                {strength.strength.toUpperCase()}
              </Typography>
              <List dense sx={{ mt: 0.5 }}>
                {PASSWORD_REQUIREMENTS.map((req) => {
                  const passed = req.test(password);
                  return (
                    <ListItem key={req.label} sx={{ py: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        {passed ? (
                          <CheckCircleIcon fontSize="small" sx={{ color: '#4caf50' }} />
                        ) : (
                          <RadioButtonUncheckedIcon fontSize="small" sx={{ color: '#bdbdbd' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={req.label}
                        primaryTypographyProps={{
                          variant: 'caption',
                          color: passed ? 'textPrimary' : 'textSecondary',
                        }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
            error={passwordMismatch}
            helperText={passwordMismatch ? 'Passwords do not match' : ''}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{ mt: 3, mb: 2, py: { xs: 1.5, sm: 1 } }}
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </Button>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" component="span" color="textSecondary">
              Already have an account?{' '}
            </Typography>
            <RouterLink to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Log in
              </Typography>
            </RouterLink>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
